import {
  authorizationSelector,
  useOrganizationIdSelector,
} from 'modules/authorization';
import { useFeatureFlag } from 'modules/feature-flags';
import { useFunction } from 'modules/firebase';
import { ProposalSegmentsController } from 'modules/proposals/hooks/useProposalSegments';
import { Service, serviceSelector, useServices } from 'modules/services';
import { subscriptionSelectors } from 'modules/subscription';
import { Unit, unitSelector, useUnits } from 'modules/units';
import { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useRecoilValue } from 'recoil';
import { toast } from 'sonner';
import { useProposalAICreatedCatalog } from '../context';
import {
  CatalogPreviewOption,
  ProposalBriefDraft,
  createCatalogPreviewOptions,
  createSlateContentFromGeneratedRichContent,
  getInitialCatalogPreviewOption,
  normalizeCatalogPreviewName,
  toBriefDraft,
  toProposalBrief,
} from '../utils';

export type ProposalAIGeneratorApplyMode = 'append' | 'replace';

export type ProposalAIGeneratorSection = 'prompt' | 'brief' | 'preview';

const ALL_EXPANDED_SECTIONS: ProposalAIGeneratorSection[] = [
  'prompt',
  'brief',
  'preview',
];

interface ServiceBlockPreviewSelection {
  service: CatalogPreviewOption<Service>;
  unit: CatalogPreviewOption<Unit>;
}

export interface ProposalAIGeneratorServicePreviewState {
  blockIndex: number;
  serviceOptions: CatalogPreviewOption<Service>[];
  unitOptions: CatalogPreviewOption<Unit>[];
  selection: ServiceBlockPreviewSelection | null;
}

function toExistingCatalogPreviewOption<T extends Service | Unit>(
  entity: T,
): CatalogPreviewOption<T> {
  return {
    kind: 'existing',
    label: entity.name,
    name: entity.name,
    value: `existing:${normalizeCatalogPreviewName(entity.name)}`,
    entity,
  };
}

export function useProposalAIGenerator(
  proposalSegments: ProposalSegmentsController,
) {
  const { control, getValues, setValue } =
    useFormContext<PublishStatusAware<Proposal>>();
  const { formatMessage, locale } = useIntl();
  const organizationId = useOrganizationIdSelector();
  const isTierOne = useRecoilValue(subscriptionSelectors.isTierOne);
  const isAIProposalGenerationEnabled = useFeatureFlag(
    'AI_PROPOSAL_GENERATION',
  );
  const { adminRole } = useSelector(authorizationSelector.getState);
  const { documents: services = [] } = useSelector(serviceSelector.getState);
  const { documents: units = [] } = useSelector(unitSelector.getState);
  const { createEntityAsync: createServiceAsync } = useServices();
  const { createEntityAsync: createUnitAsync } = useUnits();
  const { markNewlyCreatedService, markNewlyCreatedUnit } =
    useProposalAICreatedCatalog();

  const expandProposalBrief = useFunction<
    ExpandProposalBriefData,
    ProposalBrief
  >('expandProposalBrief');
  const generateProposalBlocks = useFunction<
    GenerateProposalBlocksData,
    GenerateProposalBlocksPayload
  >('generateProposalBlocks');

  const [prompt, setPrompt] = useState('');
  const [briefDraft, setBriefDraft] = useState<ProposalBriefDraft | null>(null);
  const [generated, setGenerated] =
    useState<GenerateProposalBlocksPayload | null>(null);
  const [serviceBlockSelections, setServiceBlockSelections] = useState<
    Record<number, ServiceBlockPreviewSelection>
  >({});
  const [isGeneratedStale, setIsGeneratedStale] = useState(false);
  const [isExpandingBrief, setIsExpandingBrief] = useState(false);
  const [isGeneratingBlocks, setIsGeneratingBlocks] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    ProposalAIGeneratorSection[]
  >(ALL_EXPANDED_SECTIONS);

  const currentProposalName =
    useWatch({
      control,
      name: 'name',
    }) || '';
  const currentSegmentsLength = proposalSegments.segments.length;

  const shouldRender =
    !!organizationId &&
    isAIProposalGenerationEnabled &&
    isTierOne &&
    adminRole === 'superAdmin';
  const canExpandBrief = prompt.trim().length > 0 && !isExpandingBrief;
  const hasBrief = briefDraft !== null;
  const generatedProposalNameSuggestion = useMemo(() => {
    if (!generated?.proposalNameSuggestion) {
      return briefDraft?.proposalNameSuggestion.trim() || '';
    }

    return generated.proposalNameSuggestion.trim();
  }, [briefDraft?.proposalNameSuggestion, generated?.proposalNameSuggestion]);
  const servicePreviewStates = useMemo(() => {
    if (!generated) {
      return [];
    }

    return generated.blocks.reduce<ProposalAIGeneratorServicePreviewState[]>(
      (acc, block, blockIndex) => {
        if (block.type !== 'service') {
          return acc;
        }

        const serviceOptions = createCatalogPreviewOptions(
          block.serviceName,
          services,
          (name) =>
            formatMessage(
              { id: 'proposal-ai.preview.create-service' },
              { name },
            ),
        );
        const unitOptions = createCatalogPreviewOptions(
          block.unitName,
          units,
          (name) =>
            formatMessage({ id: 'proposal-ai.preview.create-unit' }, { name }),
        );
        const initialServiceSelection = getInitialCatalogPreviewOption(
          block.serviceName,
          serviceOptions,
        );
        const initialUnitSelection = getInitialCatalogPreviewOption(
          block.unitName,
          unitOptions,
        );

        acc.push({
          blockIndex,
          serviceOptions,
          unitOptions,
          selection:
            serviceBlockSelections[blockIndex] ||
            (initialServiceSelection && initialUnitSelection
              ? {
                  service: initialServiceSelection,
                  unit: initialUnitSelection,
                }
              : null),
        });

        return acc;
      },
      [],
    );
  }, [formatMessage, generated, serviceBlockSelections, services, units]);

  function buildInitialServiceBlockSelections(payload: GenerateProposalBlocksPayload) {
    return payload.blocks.reduce<Record<number, ServiceBlockPreviewSelection>>(
      (acc, block, blockIndex) => {
        if (block.type !== 'service') {
          return acc;
        }

        const serviceOptions = createCatalogPreviewOptions(
          block.serviceName,
          services,
          (name) =>
            formatMessage(
              { id: 'proposal-ai.preview.create-service' },
              { name },
            ),
        );
        const unitOptions = createCatalogPreviewOptions(
          block.unitName,
          units,
          (name) =>
            formatMessage({ id: 'proposal-ai.preview.create-unit' }, { name }),
        );
        const service = getInitialCatalogPreviewOption(
          block.serviceName,
          serviceOptions,
        );
        const unit = getInitialCatalogPreviewOption(block.unitName, unitOptions);

        if (service && unit) {
          acc[blockIndex] = {
            service,
            unit,
          };
        }

        return acc;
      },
      {},
    );
  }

  function toggleSection(section: ProposalAIGeneratorSection) {
    setExpandedSections((currentSections) =>
      currentSections.includes(section)
        ? currentSections.filter((item) => item !== section)
        : [...currentSections, section],
    );
  }

  function updateBriefDraft<K extends keyof ProposalBriefDraft>(
    key: K,
    value: ProposalBriefDraft[K],
  ) {
    setBriefDraft((currentDraft) => {
      if (!currentDraft) return currentDraft;

      return {
        ...currentDraft,
        [key]: value,
      };
    });

    if (generated) {
      setIsGeneratedStale(true);
    }
  }

  async function handleExpandBrief() {
    if (!organizationId) return;

    try {
      setIsExpandingBrief(true);

      const { data } = await expandProposalBrief({
        organizationId,
        locale,
        prompt,
        currentProposalName: currentProposalName || null,
        clientName: getValues('client')?.name || null,
        isInvoice: getValues('isInvoice') || false,
      });

      if (!data.success) {
        toast.error(
          data.message ||
            formatMessage({ id: 'proposal-ai.toast.expand-brief-error' }),
        );
        return;
      }

      if (!data.payload) {
        toast.error(
          formatMessage({ id: 'proposal-ai.toast.expand-brief-error' }),
        );
        return;
      }

      setBriefDraft(toBriefDraft(data.payload));
      setGenerated(null);
      setIsGeneratedStale(false);
      setExpandedSections(ALL_EXPANDED_SECTIONS);
    } catch {
      toast.error(formatMessage({ id: 'proposal-ai.toast.expand-brief-error' }));
    } finally {
      setIsExpandingBrief(false);
    }
  }

  async function handleGenerateBlocks() {
    if (!briefDraft || !organizationId) return;

    const nextBrief = toProposalBrief(briefDraft);

    if (!nextBrief.projectSummary) {
      toast.error(formatMessage({ id: 'proposal-ai.toast.brief-required' }));
      return;
    }

    try {
      setIsGeneratingBlocks(true);

      const { data } = await generateProposalBlocks({
        organizationId,
        locale,
        brief: nextBrief,
        services,
        units,
      });

      if (!data.success) {
        toast.error(
          data.message ||
            formatMessage({ id: 'proposal-ai.toast.generate-blocks-error' }),
        );
        return;
      }

      if (!data.payload) {
        toast.error(
          formatMessage({ id: 'proposal-ai.toast.generate-blocks-error' }),
        );
        return;
      }

      setGenerated(data.payload);
      setServiceBlockSelections(buildInitialServiceBlockSelections(data.payload));
      setIsGeneratedStale(false);
      setExpandedSections(['preview']);
    } catch {
      toast.error(
        formatMessage({ id: 'proposal-ai.toast.generate-blocks-error' }),
      );
    } finally {
      setIsGeneratingBlocks(false);
    }
  }

  function handleServicePreviewSelectionChange(
    blockIndex: number,
    field: 'service' | 'unit',
    option: CatalogPreviewOption<Service> | CatalogPreviewOption<Unit> | null,
  ) {
    if (!option) return;

    setServiceBlockSelections((currentSelections) => {
      const currentSelection = currentSelections[blockIndex];
      if (!currentSelection) {
        return currentSelections;
      }

      return {
        ...currentSelections,
        [blockIndex]: {
          ...currentSelection,
          [field]: option,
        },
      };
    });
  }

  async function resolveServiceOption(
    option: CatalogPreviewOption<Service>,
    serviceCache: Map<string, Service>,
  ): Promise<Service | null> {
    if (option.kind === 'existing' && option.entity) {
      return option.entity;
    }

    const normalizedName = normalizeCatalogPreviewName(option.name);
    const cachedService = serviceCache.get(normalizedName);

    if (cachedService) {
      return cachedService;
    }

    const nextService = new Service({
      name: option.name,
      createdAt: Date.now(),
    });
    const createdServiceId = await createServiceAsync(nextService);

    if (!createdServiceId) {
      return null;
    }

    const createdService = {
      ...nextService,
      id: createdServiceId,
    };

    markNewlyCreatedService(createdService);
    serviceCache.set(normalizedName, createdService);

    return createdService;
  }

  async function resolveUnitOption(
    option: CatalogPreviewOption<Unit>,
    nextUnitIndexRef: { current: number },
    unitCache: Map<string, Unit>,
  ): Promise<Unit | null> {
    if (option.kind === 'existing' && option.entity) {
      return option.entity;
    }

    const normalizedName = normalizeCatalogPreviewName(option.name);
    const cachedUnit = unitCache.get(normalizedName);

    if (cachedUnit) {
      return cachedUnit;
    }

    const nextUnit = new Unit({
      name: option.name,
      index: nextUnitIndexRef.current,
      createdAt: Date.now(),
    });
    const createdUnitId = await createUnitAsync(nextUnit);

    if (!createdUnitId) {
      return null;
    }

    nextUnitIndexRef.current += 1;

    const createdUnit = {
      ...nextUnit,
      id: createdUnitId,
    };

    markNewlyCreatedUnit(createdUnit);
    unitCache.set(normalizedName, createdUnit);

    return createdUnit;
  }

  async function buildPreviewSegments() {
    if (!generated) {
      return null;
    }

    const nextUnitIndexRef = {
      current: units.length
        ? Math.max(...units.map((unit) => unit.index || 0)) + 1
        : 0,
    };

    const nextSegments: ProposalSegment[] = [];
    const nextSelections = { ...serviceBlockSelections };
    const serviceCache = new Map<string, Service>();
    const unitCache = new Map<string, Unit>();

    for (let blockIndex = 0; blockIndex < generated.blocks.length; blockIndex += 1) {
      const block = generated.blocks[blockIndex];

      if (block.type === 'heading') {
        nextSegments.push({
          type: 'heading',
          title: block.title,
          subtitle: block.subtitle,
        });
        continue;
      }

      if (block.type === 'text') {
        nextSegments.push({
          type: 'text',
          content: createSlateContentFromGeneratedRichContent(block.description),
        });
        continue;
      }

      const selection = serviceBlockSelections[blockIndex];

      if (!selection) {
        toast.error(
          formatMessage({ id: 'proposal-ai.toast.preview-selection-missing' }),
        );
        return null;
      }

      const service = await resolveServiceOption(selection.service, serviceCache);
      if (!service) {
        return null;
      }

      const unit = await resolveUnitOption(
        selection.unit,
        nextUnitIndexRef,
        unitCache,
      );
      if (!unit) {
        return null;
      }

      nextSelections[blockIndex] = {
        service: toExistingCatalogPreviewOption(service),
        unit: toExistingCatalogPreviewOption(unit),
      };

      nextSegments.push({
        type: 'service',
        service,
        unit,
        quantity: block.quantity,
        pricePerUnit: 0,
        discount: 0,
        content: createSlateContentFromGeneratedRichContent(block.description),
      });
    }

    setServiceBlockSelections(nextSelections);

    return nextSegments;
  }

  function handleApplyGenerated(mode: ProposalAIGeneratorApplyMode) {
    return async function onApplyGenerated() {
      if (!generated || isGeneratedStale) return;

      const nextSegments = await buildPreviewSegments();

      if (!nextSegments) {
        return;
      }

      if (mode === 'replace') {
        proposalSegments.replaceSegments(nextSegments);
      } else {
        proposalSegments.appendSegments(nextSegments);
      }

      if (!currentProposalName.trim() && generatedProposalNameSuggestion) {
        setValue('name', generatedProposalNameSuggestion, {
          shouldDirty: true,
        });
      }

      toast.success(formatMessage({ id: 'proposal-ai.toast.apply-success' }));
    };
  }

  function handleUseSuggestedName() {
    if (!generatedProposalNameSuggestion) return;

    setValue('name', generatedProposalNameSuggestion, {
      shouldDirty: true,
    });
  }

  function handleReset() {
    setPrompt('');
    setBriefDraft(null);
    setGenerated(null);
    setServiceBlockSelections({});
    setIsGeneratedStale(false);
    setExpandedSections(ALL_EXPANDED_SECTIONS);
  }

  return {
    briefDraft,
    canExpandBrief,
    currentProposalName,
    currentSegmentsLength,
    expandedSections,
    generated,
    generatedProposalNameSuggestion,
    handleApplyGenerated,
    handleServicePreviewSelectionChange,
    handleExpandBrief,
    handleGenerateBlocks,
    handleReset,
    handleUseSuggestedName,
    hasBrief,
    isExpandingBrief,
    isGeneratedStale,
    isGeneratingBlocks,
    prompt,
    setPrompt,
    servicePreviewStates,
    shouldRender,
    toggleSection,
    updateBriefDraft,
  };
}
